<template>
  <div
    v-if="isShowingModal"
    class="modal-mask t-flex t-justify-center"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg mw-100 w-75" role="document">
      <div class="modal-content shadow-lg border-0">
        <!-- Heading -->
        <div class="t-pb-4 t-border-b t-border-gray-700">
          <!-- Close Button -->
          <div class="t-flex t-justify-end">
            <button
              @click="onCloseModal"
              class="t-mr-4 t-mt-4 t-w-8 t-h-8 focus:t-outline-none"
            >
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>

          <div v-if="isCreatingLocation" class="t-flex t-justify-between">
            <h1
              class="t-text-center t-text-4xl t-font-medium t-tracking-wide md:t-text-left md:t-ml-12"
            >
              Add A New Location
            </h1>
            <p
              v-if="currentFormHeader === 'OwnsNewLocation'"
              class="t-flex t-items-center t-mr-16 t-rounded t-px-3 t-py-1 t-font-medium"
              style="background-color: #D0F5D7; color: #2F533F;"
            >
              LOCATION OWNED BY YOU
            </p>
          </div>
          <div v-else class="t-flex">
            <h1
              class="t-flex t-flex-grow t-text-center t-text-4xl t-font-medium t-tracking-wide md:t-text-left md:t-ml-12"
            >
              Edit Location
            </h1>

            <div
              v-if="currentFormType === 'EditOtherBusinessOwnedLocationForm'"
              class="t-flex t-flex-col"
            >
              <p
                class="t-flex t-items-center t-mr-16 t-mb-1 t-rounded t-px-3 t-py-1 t-font-medium"
                style="color: #318b89; background-color: #c5e7e8"
              >
                LOCATION OWNED BY ANOTHER
              </p>

              <div
                v-on:mouseover="active = !active"
                v-on:mouseleave="active = false"
                class="t-relative t-mr-2"
              >
                Limited Editing
                <i
                  class="fa fa-question-circle d-inline"
                  aria-hidden="true"
                  style="margin-left: 0.25rem; white-space: pre-line"
                />

                <div v-if="active" class="t-absolute t-bg-black t-text-white">
                  <div
                    class="t-text-sm t-rounded t-py-1 t-px-4 t-right-0 t-bottom-full"
                  >
                    This location has limited fields that you can edit as you
                    are not the owner. Please call us on (09) 280-3639 if there
                    is an issue.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section class="t-mx-6 md:t-mx-12 t-my-6">
          <component
            :is="currentFormType"
            :location="location"
            :isCreatingLocation="isCreatingLocation"
            :locationType="locationType"
            @changeFormType="changeFormType"
            @changeFormHeader="changeFormHeaderHandler"
            @formBackClicked="formBackHandler"
            @formSubmitted="formSubmitHandler"
            @onDeleteLocationClick="onDeleteLocationHandler"
          ></component>
        </section>
      </div>
    </div>

    <confirm-delete
      v-if="isShowingDeleteModal"
      :showing="isShowingDeleteModal"
      title="Delete this location?"
      :text="'Are you sure you want to delete ' + this.location.name + '?'"
      :deleting="isDeletingLocation"
      @confirmed="deleteConfirmed"
      @cancelled="deleteCancelled"
    />
  </div>
</template>

<script>
import Autocomplete from "../../../utils/Autocomplete";
import EnterManualLocationForm from "./EnterManualLocationForm";
import SearchLocationForm from "./forms/SearchLocationForm";
import AddFreighthubLocationForm from "./forms/AddFreighthubLocationForm";
import EditOtherBusinessOwnedLocationForm from "./forms/EditOtherBusinessOwnedLocationForm";
import SaveLocationForm from "./forms/SaveLocationForm";
import ConfirmDelete from "../../../utils/ConfirmDelete";

export default {
  name: "SaveLocationModal",
  description: "",
  components: {
    SaveLocationForm,
    Autocomplete,
    EnterManualLocationForm,
    SearchLocationForm,
    AddFreighthubLocationForm,
    EditOtherBusinessOwnedLocationForm,
    ConfirmDelete
  },
  emits: ["cancelled"],
  props: {
    isShowingModal: {
      type: Boolean,
      required: true
    },
    /**
     * Either creating or editing a location
     */
    isCreatingLocation: {
      type: Boolean,
      required: true
    },
    /**
     * A given prop if a location is being edited
     */
    locationToEdit: {
      type: Object,
      default: null
    },
    locationType: {
      type: String
    }
  },
  data() {
    return {
      active: false,
      currentFormType: "SearchLocationForm",
      currentFormHeader: "",
      isSearchingLocation: this.isCreatingLocation === true,
      searchLocationSelected: false,
      isManuallyEnteringLocation: false,
      isValidLocation: false,
      isShowingDeleteModal: false,
      isDeletingLocation: false,
      search: {
        results: [],
        showResults: false,
        autocompleteService: null,
        placesService: null,
        locationSelected: false
      },
      location: {
        name: "",
        address: {
          data: {
            street_name: null,
            street_number: null,
            suburb: null,
            city: null,
            latitude: null,
            longitude: null,
            post_code: null
          }
        },
        openingHours: {
          data: []
        }
      },
      missingLocationFields: null,
      errorMessage: null
    };
  },
  created() {
    if (this.locationToEdit) {
      if (
        this.locationToEdit.ownershipStatus === "OWNED_BY_YOU" ||
        this.locationToEdit.ownershipStatus === "OWNED_BY_NO_ONE"
      ) {
        this.location = this.locationToEdit;
        this.currentFormType = "SaveLocationForm";
      } else {
        this.location = this.locationToEdit;
        this.currentFormType = "EditOtherBusinessOwnedLocationForm";
      }
    }
  },
  methods: {
    changeFormType(formType, location) {
      this.currentFormType = formType;
      this.location = location;
    },

    changeFormHeaderHandler(formHeader) {
      this.currentFormHeader = formHeader;
    },

    formBackHandler() {
      this.changeFormType("SearchLocationForm", null);
      this.resetForm();
    },

    formSubmitHandler() {
      this.onCloseModal();
    },

    onEnterManualLocationClick() {
      this.isSearchingLocation = false;
      this.isManuallyEnteringLocation = true;

      if (this.isManuallyEnteringLocation) {
        this.missingLocationFields = {
          name: true,
          address: {
            street_number: true,
            street_name: true,
            suburb: true,
            city: true
          }
        };
      }
    },

    onEnterManualLocationCancelled() {
      this.resetForm();
    },

    onEnterManualLocationConfirmed(location) {
      this.location = {
        ...this.location,
        name: location.name,
        address: {
          data: { ...location.address }
        }
      };

      this.errorMessage = null;
      this.isValidLocation = true;
    },

    onDeleteLocationHandler() {
      this.isShowingDeleteModal = true;
    },

    deleteCancelled() {
      this.isShowingDeleteModal = false;
    },

    deleteConfirmed() {
      this.isDeleting = true;

      axios
        .delete("/settings/locations/detach/" + this.location.id, {
          headers: { Accept: "application/json" }
        })
        .then(() => {
          this.isShowingDeleteModal = false;
          this.isDeleting = false;
          this.$swal({
            title: "Location Deleted!",
            type: "success",
            confirmButtonText: "Okay"
          });
          this.onCloseModal();
          this.$emit("submitted");
        })
        .catch(() => {
          this.isShowingDeleteModal = false;
          this.isDeleting = false;
        });
    },

    onCloseModal() {
      this.resetForm();
      this.$emit("cancelled");
    },

    resetForm() {
      this.location = null;

      this.formType = null;
      this.currentFormHeader = "";
      this.currentFormType = "SearchLocationForm";

      this.isSearchingLocation = true;
      this.searchLocationSelected = false;
      this.search.results = [];
      this.search.locationSelected = false;

      this.isManuallyEnteringLocation = false;
      this.isValidLocation = false;

      this.errorMessage = null;
      this.missingLocationFields = null;

      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.clear();
        this.$refs.autocomplete.clearDisplay();
      }
    }
  }
};
</script>

<style lang="scss">
$md: 768px;
$dark-gray: #1f2937;

.location-form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  label {
    margin-bottom: 0;
    font-weight: 600;
    color: $dark-gray;
  }

  > input,
  p.value {
    border: 1px solid #cbd5e0;
    border-radius: 0.375rem;
    width: 100%;
    padding: 0.25rem 0.5rem;

    :focus {
      outline: none;
      border: 1px solid #667eea;
    }
  }

  &__heading {
    display: flex;
    flex-direction: column;

    @media (min-width: $md) {
      flex-direction: row;
      justify-content: space-between;
    }
  }
}

.location-form-option {
  display: flex;
  margin-bottom: 0.5rem;

  > input {
    margin-top: 0.25rem;
    margin-right: 1rem;
    width: 1rem;
    height: 1rem;
  }

  label {
    margin-bottom: 0;
    font-weight: 500;
    color: $dark-gray;
  }
}

.tab-active {
  border-bottom: 2px solid #17a8e2;
}
</style>
